import React, { useState } from 'react';
import axios from 'axios';
import { GearSix, Plus, Trash } from '@phosphor-icons/react';
import { notify } from '../../lib/notify';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const input = 'w-full px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30';

// Cost assumptions: installation/warranty %, commission % per role, monthly office costs.
export default function ProfitSettingsPanel({ settings, onSaved }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  React.useEffect(() => setForm(settings), [settings]);

  const setComm = (role, v) => setForm({ ...form, commission_pct: { ...form.commission_pct, [role]: v } });
  const setOffice = (i, k, v) => setForm({ ...form, office_costs: form.office_costs.map((r, j) => (j === i ? { ...r, [k]: v } : r)) });
  const addOffice = () => {
    const d = new Date();
    setForm({ ...form, office_costs: [...form.office_costs, { month: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`, amount: '', note: '' }] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/profitability/settings`, {
        installation_warranty_pct: Number(form.installation_warranty_pct || 0),
        commission_pct: Object.fromEntries(Object.entries(form.commission_pct).map(([r, v]) => [r, Number(v || 0)])),
        office_costs: form.office_costs.map((r) => ({ ...r, amount: Number(r.amount || 0) })),
        loss_alert_pct: form.loss_alert_pct === '' || form.loss_alert_pct == null ? null : Number(form.loss_alert_pct),
      });
      notify('Cost settings saved', 'success');
      onSaved();
    } catch (e) {
      notify(e.response?.data?.detail || 'Could not save settings', 'error');
    } finally { setSaving(false); }
  };

  return (
    <div className="medical-card p-4" data-testid="profit-settings-panel">
      <button type="button" onClick={() => setOpen(!open)} className="flex items-center gap-2 text-sm font-semibold text-[#052A23]" data-testid="profit-settings-toggle">
        <GearSix size={18} /> Cost settings {open ? '▴' : '▾'}
        <span className="text-xs font-normal text-[#6D8E87]">
          Install & warranty {settings.installation_warranty_pct}% · {settings.office_costs.length} month(s) of office costs
          · loss alert {settings.loss_alert_pct == null ? 'off' : `below ${settings.loss_alert_pct}%`}
          {settings.updated_by ? ` · last edited by ${settings.updated_by}` : ''}
        </span>
      </button>
      {open && (
        <div className="grid lg:grid-cols-3 gap-6 mt-4">
          <div>
            <label className="block text-xs font-semibold text-[#3B5A54] mb-1">Installation & warranty allowance (% of revenue)</label>
            <input type="number" min="0" max="100" step="0.1" className={input} value={form.installation_warranty_pct}
              onChange={(e) => setForm({ ...form, installation_warranty_pct: e.target.value })} data-testid="profit-install-pct-input" />
            <label className="block text-xs font-semibold text-[#3B5A54] mt-4 mb-1">Loss alert: notify VP & President when a won order's contribution is below (%)</label>
            <input type="number" min="-100" max="100" step="0.5" className={input} value={form.loss_alert_pct ?? ''} placeholder="Off"
              onChange={(e) => setForm({ ...form, loss_alert_pct: e.target.value })} data-testid="profit-loss-alert-input" />
            <p className="text-[11px] text-[#6D8E87] mt-1">Leave blank to switch alerts off. Use 0 to alert only on actual losses.</p>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#3B5A54] mb-1">Sales commission (% of revenue, by order owner's role)</p>
            <div className="space-y-1.5">
              {Object.keys(form.commission_pct).map((role) => (
                <div key={role} className="flex items-center gap-2">
                  <span className="flex-1 text-xs text-[#3B5A54]">{role}</span>
                  <input type="number" min="0" max="100" step="0.1" className="w-24 px-3 py-2 border border-[#D5E8E4] rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#00a69c]/30" value={form.commission_pct[role]}
                    onChange={(e) => setComm(role, e.target.value)} data-testid={`profit-comm-${role.toLowerCase().replace(/\s+/g, '-')}`} />
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold text-[#3B5A54] mb-1">Monthly office costs (rent, travel, utilities…)</p>
            <div className="space-y-1.5">
              {form.office_costs.map((r, i) => (
                <div key={i} className="flex items-center gap-1.5" data-testid={`profit-office-row-${i}`}>
                  <input type="month" className={`${input} w-36`} value={r.month} onChange={(e) => setOffice(i, 'month', e.target.value)} data-testid={`profit-office-month-${i}`} />
                  <input type="number" min="0" placeholder="₹" className={`${input} w-28`} value={r.amount} onChange={(e) => setOffice(i, 'amount', e.target.value)} data-testid={`profit-office-amount-${i}`} />
                  <input type="text" placeholder="Note" className={input} value={r.note} onChange={(e) => setOffice(i, 'note', e.target.value)} />
                  <button type="button" onClick={() => setForm({ ...form, office_costs: form.office_costs.filter((_, j) => j !== i) })} className="p-1.5 text-rose-600" data-testid={`profit-office-remove-${i}`}><Trash size={14} /></button>
                </div>
              ))}
              <button type="button" onClick={addOffice} className="inline-flex items-center gap-1 text-xs font-semibold text-[#00a69c]" data-testid="profit-office-add-btn"><Plus size={12} /> Add month</button>
            </div>
          </div>
          <div className="lg:col-span-3 flex justify-end">
            <button type="button" onClick={save} disabled={saving} className="px-4 py-2 rounded-lg bg-[#00a69c] hover:bg-[#008c84] text-white text-sm font-semibold disabled:opacity-60" data-testid="profit-settings-save-btn">
              {saving ? 'Saving…' : 'Save cost settings'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
